import Styles from '../../../3.recruit/styles.module.css';
import Styless from '../../../styles.module.css';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { doc, getFirestore, onSnapshot, setDoc } from 'firebase/firestore';
import Summary from '../../../3.recruit/2.edit/1.summary';
import Stores from '../../../3.recruit/2.edit/2.store';
import Point from '../../../3.recruit/2.edit/3.point';
import Number from '../../../3.recruit/2.edit/4.number';
import Photos from '../../../3.recruit/2.edit/5.strong';
import Interview from '../../../3.recruit/2.edit/6.interview';
import Person from '../../../3.recruit/2.edit/7.person';
import Document from '../../../3.recruit/2.edit/8.document';
import BackCaution from '../../../3.recruit/2.edit/backCaution/index';
import Navigation from './navigation/index';
import Slider from 'src/pages/recruit/recruit/4.strongImage/index';
import RecruitPageHeader from 'src/pages/recruit/recruit/0.header';
import RecruitPageTitle from 'src/pages/recruit/recruit/title';
import RecruitPagePoint from 'src/pages/recruit/recruit/1.point/index';
import RecruitPageNumber from 'src/pages/recruit/recruit/3.number';
import RecruitPageStores from 'src/pages/recruit/recruit/2.stores/index';
import RecruitPagesInterviews from 'src/pages/recruit/recruit/5.interviews';
import RecruitPagesPerson from 'src/pages/recruit/recruit/6.person';
import { defaultDocumentData, defaultInterviewsData, defaultNumberData, defaultPamphlet, defaultPersonData, defaultPointData, defaultStrongData, defaultSummaryData, formatDocument, formatInterviews, formatNumber, formatPamphlet, formatPerson, formatPoint, formatStrong, formatSummary } from 'src/type';
import { useAppSelector } from 'src/salon/app/hook';
import { isMidCareerPositionSlug, midCareerRecruitId } from '../../positions';

// 中途採用(スタイリスト/アシスタント)の「求人の編集」画面。
// 新卒側(3.recruit/index.tsx)の各セクション編集コンポーネント・プレビューを
// そのまま再利用し、書き込み先のドキュメントIDだけを
// recruits/{companyId}_midCareer_{position} に向けている。
//
// 新卒側と異なり、公開(recruitStatus=1)・searchIndex登録・学校送付とは
// 一切連携しない(アプリ側が中途採用に未対応のため)。ステータスによる
// 画面出し分けも行わず、常に編集画面を表示する。

const MidCareerRecruitEdit: React.FC = () => {
  const db = getFirestore();
  const company = useAppSelector((state) => state.company.company);
  const { position } = useParams<{ position: string }>();

  const [navigation, setNavigation] = useState<number>(1);

  const [summary, setSummary] = useState(defaultSummaryData);
  const memoizedSummary = useMemo(() => summary, [summary]);

  type storeData = { isCompleted: boolean };
  const [store, setStore] = useState<storeData>({ isCompleted: false });

  const [point, setPoint] = useState(defaultPointData);
  const memoizedPoint = useMemo(() => point, [point]);

  const [number, setNumber] = useState(defaultNumberData);
  const memoizedNumber = useMemo(() => number, [number]);

  const [strong, setStrong] = useState(defaultStrongData);
  const memoizedStrong = useMemo(() => strong, [strong]);

  const [interviews, setInterviews] = useState(defaultInterviewsData);
  const memoizedInterviews = useMemo(() => interviews, [interviews]);

  const [interviewImage1File, setInterviewImage1File] = useState<File | null>(null);
  const [interviewImage2File, setInterviewImage2File] = useState<File | null>(null);
  const [interviewImage3File, setInterviewImage3File] = useState<File | null>(null);
  const [interviewImage4File, setInterviewImage4File] = useState<File | null>(null);
  const [interviewImage5File, setInterviewImage5File] = useState<File | null>(null);
  const [interviewImage6File, setInterviewImage6File] = useState<File | null>(null);

  const [person, setPerson] = useState(defaultPersonData);
  const memoizedPerson = useMemo(() => person, [person]);

  const [documentData, setDocumentData] = useState(defaultDocumentData);
  const memoizedDocument = useMemo(() => documentData, [documentData]);

  const [progress, setProgress] = useState<boolean[]>(Array(8).fill(false));
  const [changeData, setChangeData] = useState<boolean>(false);
  const [backCaution, setBackCaution] = useState<number>(0);
  const [published, setPublished] = useState<boolean>(false);

  const [storeImageLocal, setStoreImageLocal] = useState<string>('');

  const [pamphlet, setPamphlet] = useState(defaultPamphlet);
  const memoizedPamphlet = useMemo(() => pamphlet, [pamphlet]);

  const navRef = useRef<HTMLDivElement>(null);

  const documentId = position && company?.id && isMidCareerPositionSlug(position)
    ? midCareerRecruitId(company.id, position)
    : '';

  useEffect(() => {
    if (!documentId || !company?.id) return;

    // firestore.rulesのhasExpectedRecruitId()で許可されたID規則
    // (companyId + "_midCareer_stylist" 等)に沿って初回のみ作成する。
    // merge:trueなので既存データは壊さない。
    setDoc(doc(db, 'recruits', documentId), {
      companyId: company.id,
      id: documentId,
      recruitStatus: 3,
    }, { merge: true }).catch((error) => {
      console.error('中途採用求人データの初期化に失敗しました:', error);
    });

    const unsubscribe = onSnapshot(doc(db, 'recruits', documentId), (docSnap) => {
      if (!docSnap.exists()) return;
      const docData = docSnap.data();

      setProgress([
        docData.summary?.isCompleted && docData.summary.images?.length > 0,
        docData.store?.isCompleted,
        docData.point?.isCompleted,
        docData.number?.isCompleted,
        docData.strong?.isCompleted,
        docData.interviews?.isCompleted,
        docData.person?.isCompleted,
        docData.document?.isCompleted,
      ]);

      setPublished(docData.recruitStatus === 1);
      setStore({ isCompleted: docData.store?.isCompleted ?? false });
      setSummary(formatSummary(docData));
      setPoint(formatPoint(docData));
      setNumber(formatNumber(docData));
      setStrong(formatStrong(docData));
      setInterviews(formatInterviews(docData));
      setPerson(formatPerson(docData));
      setDocumentData(formatDocument(docData));
      setPamphlet(formatPamphlet(docData));
    });

    return () => unsubscribe();
  }, [documentId, company.id]);

  if (!position || !isMidCareerPositionSlug(position)) {
    return <Navigate to="/service/midCareer/recruit" replace />;
  }

  const isWindows = navigator.userAgent.includes('Windows');
  const zoom = isWindows ? 1.25 : 1.0;

  return (
    <>
    <div className={Styless.works_service}>
      <div>
        <div className={Styles.box}>

          <div ref={navRef} className={Styles.desktop_nav}>
            <Navigation
              progress={progress}
              changeData={changeData} navigation={navigation} setNavigation={setNavigation} setBackCaution={setBackCaution}
              documentId={documentId} published={published} />
          </div>

          <div className={Styles.center}>
            {navigation === 1 ?
            <Summary documentId={documentId} setChangeData={setChangeData} summary={memoizedSummary} setSummary={setSummary} />
            : navigation === 2 ?
            <Stores documentId={documentId} setStoreImageLocal={setStoreImageLocal} />
            : navigation === 3 ?
            <Point documentId={documentId} setChangeData={setChangeData} point={memoizedPoint} setPoint={setPoint} />
            : navigation === 4 ?
            <Number documentId={documentId} setChangeData={setChangeData} number={memoizedNumber} setNumber={setNumber} />
            : navigation === 5 ?
            <Photos documentId={documentId} setChangeData={setChangeData} strong={memoizedStrong} setStrong={setStrong} />
            : navigation === 6 ?
            <Interview
              documentId={documentId}
              setChangeData={setChangeData}
              interviews={memoizedInterviews} setInterviews={setInterviews}
              interviewImage1File={interviewImage1File} setInterviewImage1File={setInterviewImage1File}
              interviewImage2File={interviewImage2File} setInterviewImage2File={setInterviewImage2File}
              interviewImage3File={interviewImage3File} setInterviewImage3File={setInterviewImage3File}
              interviewImage4File={interviewImage4File} setInterviewImage4File={setInterviewImage4File}
              interviewImage5File={interviewImage5File} setInterviewImage5File={setInterviewImage5File}
              interviewImage6File={interviewImage6File} setInterviewImage6File={setInterviewImage6File} />
            : navigation === 7 ?
            <Person documentId={documentId} setChangeData={setChangeData} person={memoizedPerson} setPerson={setPerson} />
            : navigation === 8 ?
            <Document documentId={documentId} setChangeData={setChangeData} document={memoizedDocument} setDocument={setDocumentData} />
            : <></>}

            <div style={{ height: '30px' }}></div>
          </div>

          <div className={Styles.desktop_preview}>
            <div className={Styles.preview} style={isWindows ? { zoom, height: '480px' } : { zoom }}>

              <RecruitPageHeader companyId={company.id ?? ''} summary={memoizedSummary} recruitStatus={false} preview />

              <RecruitPageTitle title="魅力3選" desc="サロンが思う自社の魅力を3つにまとめて紹介" />
              <RecruitPagePoint point={memoizedPoint} pamphlet={memoizedPamphlet} preview />

              <div className={Styles.hr}></div>
              <RecruitPageTitle title="店舗紹介" desc="企業が運営しているサロンを全て紹介" />
              <RecruitPageStores companyId={company.id} searchType={''} searchPref={''} />

              <div className={Styles.hr}></div>
              <RecruitPageTitle title="数字で見る" desc="魅力を伝わりやすく数字で表現" />
              <RecruitPageNumber number={memoizedNumber} preview />

              <div className={Styles.hr}></div>
              <RecruitPageTitle title="写真で見る" desc="魅力を写真で表現" />

              <div className={Styles.image_appeal}>
                <Slider imageName={memoizedStrong.item1.images} number={1} preview />
                <h4>{memoizedStrong.item1.title}</h4>
                <p>{memoizedStrong.item1.desc}</p>
              </div>
              <div className={Styles.image_appeal}>
                <Slider imageName={memoizedStrong.item2.images} number={2} preview />
                <h4>{memoizedStrong.item2.title}</h4>
                <p>{memoizedStrong.item2.desc}</p>
              </div>
              <div className={Styles.image_appeal}>
                <Slider imageName={memoizedStrong.item3.images} number={3} preview />
                <h4>{memoizedStrong.item3.title}</h4>
                <p>{memoizedStrong.item3.desc}</p>
              </div>

              <div className={Styles.hr}></div>
              <RecruitPageTitle title="インタビュー" desc="今、現役で働いている方の声を掲載" />
              <RecruitPagesInterviews interviews={memoizedInterviews} preview />

              <div className={Styles.hr}></div>
              <RecruitPageTitle title="求める人物像" desc="求めている人物像を数値化" />
              <RecruitPagesPerson person={memoizedPerson} />

              <div style={{ height: '100px' }}></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    {backCaution !== 0 ?
      <BackCaution backCaution={backCaution} setBackCaution={setBackCaution} setNavigation={setNavigation} setChangeData={setChangeData} />
    : <></>}
    </>
  );
};

export default MidCareerRecruitEdit;
